import { useEffect, useRef, useState } from "react";
import {
  START_AFTER_CHECKOUT, clearPendingJob, onResumeStatus, readPendingJob, registerPayJob, resumeStatus, setResumeStatus,
  type PayJob, type PayStudio, type PendingJob,
} from "@/lib/pay-sheet";

/**
 * Whatever holds the job as the buyer edited it (a plan card, the corrido
 * board) tells the pay sheet how to read it, so it is kept through checkout.
 */
export function usePayJob(studio: PayStudio, read: () => PayJob | null) {
  const ref = useRef(read);
  ref.current = read;
  useEffect(() => registerPayJob(studio, () => ref.current()), [studio]);
}

/**
 * A studio flow's side of the pay sheet. Back from checkout, once `ready` (signed
 * in, prices loaded, a corrido board reopened): `start` runs the paid job,
 * `restore` only puts it back on screen (checkout cancelled, the payment
 * still clearing, or START_AFTER_CHECKOUT off).
 */
export function useResumeJob(
  studio: PayStudio,
  ready: boolean,
  handlers: { start: (job: PendingJob) => void; restore: (job: PendingJob) => void },
) {
  const ref = useRef(handlers);
  ref.current = handlers;

  const [status, setStatus] = useState(resumeStatus());
  useEffect(() => {
    // A status set between this render and this effect is not missed.
    setStatus(resumeStatus());
    return onResumeStatus(() => setStatus(resumeStatus()));
  }, []);

  useEffect(() => {
    if (!ready || status === "idle") return;
    const job = readPendingJob();
    if (!job || job.studio !== studio) return;
    clearPendingJob();
    setResumeStatus("idle");
    if (status === "covered" && START_AFTER_CHECKOUT) ref.current.start(job);
    else ref.current.restore(job);
  }, [ready, status, studio]);
}
